import React from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import { LANDING_PRICING, LANDING_PRICING_CURRENCY } from '../../constants/LandingPricing';
import { formatCurrency } from '../../utils/currencyUtils';
import ProCrownIcon from './ProCrownIcon';

export const PRO_UPGRADE_VALUE_KEYS = ['stores', 'professionals', 'dashboard', 'payments'];

export const PRO_UPGRADE_FEATURE_KEYS = [
  'deposits',
  'installments',
  'refunds',
  'productsStock',
  'membershipPlans',
  'teamPayroll',
  'waitlistHours',
  'additionalCosts',
  'customPermissions',
  'smsReminders',
  'whatsappReminders',
  'customBranding',
  'prioritySupport',
];

const ProUpgradeDialog = ({
  open,
  onClose,
  onStartPro,
  checkoutLoading = false,
  isLapsed = false,
  isOwner = false,
}) => {
  const { t } = useTranslation();
  const introPrice = formatCurrency(LANDING_PRICING.introductoryMonthly, LANDING_PRICING_CURRENCY);
  const basePrice = formatCurrency(LANDING_PRICING.baseSubscriptionMonthly, LANDING_PRICING_CURRENCY);
  const canCheckout = typeof onStartPro === 'function' && isOwner;

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      showCloseButton
      paperBorderRadius={BRAND_RADIUS.card}
      paperSx={{
        backgroundColor: BRAND.background,
        border: `1px solid ${BRAND.line}`,
        boxShadow: `0 24px 48px ${brandAlpha('primaryDark', 0.35)}`,
      }}
      titleSx={{
        pb: 1.5,
        pt: 2,
        flexShrink: 0,
        background: BRAND.primary,
        color: BRAND.onPrimary,
        '& .MuiIconButton-root': {
          color: BRAND.onPrimary,
        },
      }}
      contentSx={{ backgroundColor: BRAND.background }}
      actionsSx={{
        flexShrink: 0,
        justifyContent: 'space-between',
        backgroundColor: BRAND.surface,
        borderColor: BRAND.line,
      }}
      title={(
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ProCrownIcon size={22} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: BRAND.accentLight,
              }}
            >
              {t('storePlan.upgradeDialog.eyebrow')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.2rem', sm: '1.45rem' }, color: BRAND.onPrimary }}>
              {isLapsed ? t('storePlan.upgradeDialog.lapsedTitle') : t('storePlan.upgradeDialog.title')}
            </Typography>
          </Box>
        </Stack>
      )}
      actions={(
        <>
          <Button color="inherit" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('common.cancel')}
          </Button>
          {canCheckout ? (
            <PrimaryActionButton
              onClick={onStartPro}
              disabled={checkoutLoading}
              startIcon={checkoutLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {checkoutLoading
                ? t('stores.checkout.opening')
                : (isLapsed ? t('storePlan.reactivateAction') : t('storePlan.upgradeToPro'))}
            </PrimaryActionButton>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, textAlign: 'right' }}>
              {t('storePlan.upgradeDialog.askOwner')}
            </Typography>
          )}
        </>
      )}
    >
      <Typography sx={{ color: BRAND.text, fontWeight: 600, mb: 0.75 }}>
        {t('storePlan.upgradeDialog.subtitle')}
      </Typography>
      <Typography variant="body2" sx={{ color: BRAND.textMuted, mb: 2.5 }}>
        {t('storePlan.upgradeDialog.priceNote', {
          introPrice,
          regularPrice: basePrice,
          months: LANDING_PRICING.introductoryPeriodMonths,
        })}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1,
        }}
      >
        {PRO_UPGRADE_VALUE_KEYS.map((key) => (
          <AdvantageRow key={`value-${key}`} label={t(`landing.pricing.plans.pro.values.${key}`)} />
        ))}
        {PRO_UPGRADE_FEATURE_KEYS.map((key) => (
          <AdvantageRow key={key} label={t(`landing.pricing.features.${key}`)} />
        ))}
      </Box>
    </ScrollableDialog>
  );
};

const AdvantageRow = ({ label }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="flex-start"
    sx={{
      py: 0.75,
      px: 1,
      borderRadius: BRAND_RADIUS.button,
      backgroundColor: BRAND.surface,
      border: `1px solid ${BRAND.line}`,
    }}
  >
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        mt: '1px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: BRAND.accent,
        color: BRAND.onAccent,
      }}
    >
      <CheckRoundedIcon sx={{ fontSize: 14 }} />
    </Box>
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: BRAND.text, lineHeight: 1.35 }}>
      {label}
    </Typography>
  </Stack>
);

export default ProUpgradeDialog;
